import React, { useState } from "react";
import { View, StyleSheet, Text, ScrollView } from "react-native";
import WorkspaceCard from "../WorkspaceCard/WorkspaceCard";
import { colors, spacing, typographyScale, radii } from "../../../styles/design-system";
import { AxioLogo } from "../../svgs/logos";
import { PanelLeft, PanelLeftClose, X } from "lucide-react";
import Tooltip from "../../common/tooltip/Tooltip";
import { useAppConfig } from "../../../AppConfigProvider";
import CustomButton from "../../common/general/CustomButton/CustomButton";
import useResponsiveScreen from "../../../hooks/useResponsiveScreen";
import { homeWorkspace } from "../../../hooks/useWorkspaceLists";
import Skeleton from "../Skeleton/Skeleton";
import type { WorkspaceSidebarWorkspace } from "../types";

const WorkspaceSkeleton: React.FC<{ isSidebarCollapsed: boolean }> = ({ isSidebarCollapsed }) => {
  return (
    <View style={styles.skeletonCard}>
      <View style={styles.skeletonLeftSection}>
        <Skeleton width={spacing.xl} height={spacing.xl} borderRadius={radii.md} />
        {!isSidebarCollapsed && (
          <Skeleton width="100%" height={spacing.xl} borderRadius={radii.md} style={styles.skeletonText} />
        )}
      </View>
    </View>
  );
};

export interface WorkspaceSidebarFooterItem {
  id: string;
  label: string;
}

export interface WorkspaceSidebarProps {
  generalWorkspaces: WorkspaceSidebarWorkspace[];
  courseWorkspaces: WorkspaceSidebarWorkspace[];
  todoWorkspaces: WorkspaceSidebarWorkspace[];
  learningActivitiesWorkspaces: WorkspaceSidebarWorkspace[];
  settingsWorkspaces: WorkspaceSidebarWorkspace[];
  activeWorkspaceId?: string;
  onWorkspacePress?: (id: string) => void;
  threadsByWorkspaceId?: Record<
    string,
    {
      threads: Array<{ id: string; title: string; metaLabel?: string; isActive: boolean }>;
      totalCount: number;
      hasMore: boolean;
      isLoadingMore: boolean;
    }
  >;
  isLoadingCourseWorkspaces?: boolean;
  onThreadSelect?: (threadId: string) => void;
  onThreadPin?: (threadId: string) => void;
  onThreadRename?: (threadId: string, newName: string) => void;
  onThreadDelete?: (threadId: string) => void;
  onNewThread?: (workspaceId: string) => void;
  onThreadLoadMore?: (workspaceId: string) => void;
  /** ID of the currently active thread - used for auto-expanding thread lists */
  activeThreadId?: string | null;
  isSidebarCollapsed?: boolean;
  setIsSidebarCollapsed?: (value: boolean) => void;
  openWorkspaceId?: string | null;
  toggleWorkspaceDrawer?: (workspaceId: string) => void;
}

export const EXPANDED_WIDTH = 272;
export const COLLAPSED_WIDTH = 67;

const WorkspaceSidebar: React.FC<WorkspaceSidebarProps> = ({
  generalWorkspaces,
  courseWorkspaces,
  todoWorkspaces,
  learningActivitiesWorkspaces,
  settingsWorkspaces,
  activeWorkspaceId,
  onWorkspacePress,
  threadsByWorkspaceId = {},
  isLoadingCourseWorkspaces = false,
  onThreadSelect,
  onThreadPin,
  onThreadRename,
  onThreadDelete,
  onNewThread,
  onThreadLoadMore,
  activeThreadId,
  isSidebarCollapsed = false,
  setIsSidebarCollapsed = () => {},
  openWorkspaceId = null,
  toggleWorkspaceDrawer = () => {},
}) => {
  const [isHovered, setIsHovered] = useState(false);

  const { isLarge } = useResponsiveScreen();

  const { isLTI, ltiLaunchUrl } = useAppConfig();

  const handleExternalPress = () => {
    if (isLTI && ltiLaunchUrl) {
      window.open(ltiLaunchUrl, "_blank");
    }
  };

  const handleWorkspacePress = (id: string, action: (id: string) => void) => {
    if (!isLarge) {
      setIsSidebarCollapsed(true);
    }
    action?.(id);
  };

  const renderWorkspaceSection = (label: string, workspaces: WorkspaceSidebarWorkspace[], isLoading?: boolean) => {
    const shouldShowLabel = !isSidebarCollapsed && label !== "GENERAL";
    const sectionGap = isSidebarCollapsed ? 4 : 2; // space-y-1 (4px) when collapsed, space-y-0.5 (2px) when expanded

    if (isLoading && !workspaces.length) {
      return (
        <View style={[styles.sectionCards, { gap: sectionGap }]}>
          {shouldShowLabel && <Text style={styles.sectionLabel}>{label}</Text>}
          {[0, 1, 2].map((index) => (
            <WorkspaceSkeleton key={index} isSidebarCollapsed={isSidebarCollapsed} />
          ))}
        </View>
      );
    }

    if (!workspaces.length) return null;

    return (
      <View style={[styles.sectionCards, { gap: sectionGap }]}>
        {shouldShowLabel && <Text style={styles.sectionLabel}>{label}</Text>}
        {workspaces.map((workspace) => {
          const threadData = threadsByWorkspaceId[workspace.id];
          const threads = threadData?.threads ?? [];
          const hasThreads = threads.length > 0;
          const totalCount = threadData?.totalCount ?? threads.length;
          const hasMoreThreads = threadData?.hasMore ?? threads.length < totalCount;
          const isLoadingMore = threadData?.isLoadingMore ?? false;
          const isAccordionExpanded = openWorkspaceId === workspace.id;
          const action = hasThreads
            ? (id: string) => {
                const expanding = openWorkspaceId !== id;
                toggleWorkspaceDrawer?.(id);
                if (expanding) onWorkspacePress?.(id);
              }
            : (id: string) => onWorkspacePress?.(id);

          return (
            <WorkspaceCard
              key={workspace.id}
              {...workspace}
              isActive={workspace.id === activeWorkspaceId}
              onPress={() => handleWorkspacePress(workspace.id, action)}
              isSidebarCollapsed={isSidebarCollapsed}
              threads={hasThreads ? threads : undefined}
              isAccordionExpanded={hasThreads ? isAccordionExpanded : undefined}
              onThreadSelect={hasThreads ? onThreadSelect : undefined}
              onThreadPin={hasThreads ? onThreadPin : undefined}
              onThreadRename={hasThreads ? onThreadRename : undefined}
              onThreadDelete={hasThreads ? onThreadDelete : undefined}
              onNewThread={hasThreads ? onNewThread : undefined}
              threadTotalCount={hasThreads ? totalCount : undefined}
              hasMoreThreads={hasThreads ? hasMoreThreads : undefined}
              isThreadLoading={hasThreads ? isLoadingMore : undefined}
              onThreadLoadMore={hasThreads ? onThreadLoadMore : undefined}
              activeThreadId={hasThreads ? activeThreadId : undefined}
            />
          );
        })}
      </View>
    );
  };

  return (
    <View style={styles.content}>
      <View
        style={[
          styles.container,
          !isLarge && styles.containerSmall,
          isLarge
            ? { width: isSidebarCollapsed ? COLLAPSED_WIDTH : EXPANDED_WIDTH }
            : { width: EXPANDED_WIDTH, transform: [{ translateX: isSidebarCollapsed ? -EXPANDED_WIDTH : 0 }] },
        ]}>
        <View style={styles.logoSection}>
          <AxioLogo height={28} />
          {isLarge ? (
            <Tooltip
              content={isSidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
              placement="right"
              horizontalOffset={5}
              showArrow={false}>
              <CustomButton
                styleType="secondaryLightened"
                size="s"
                style={styles.sidebarIcon}
                leftIcon={
                  isSidebarCollapsed ? (
                    <PanelLeft size={20} color={isHovered ? colors.grey[700] : colors.grey[500]} />
                  ) : (
                    <PanelLeftClose size={20} color={isHovered ? colors.grey[700] : colors.grey[500]} />
                  )
                }
                onHover={setIsHovered}
                onPress={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
                accessibilityLabel="Toggle sidebar"
                accessibilityHint="Toggle the sidebar visibility"
              />
            </Tooltip>
          ) : (
            <CustomButton
              styleType="secondaryLightened"
              size="s"
              style={styles.sidebarIcon}
              leftIcon={<X size={20} color={isHovered ? colors.grey[700] : colors.grey[500]} />}
              onPress={() => setIsSidebarCollapsed(true)}
              accessibilityLabel="Close sidebar"
              accessibilityHint="Close the sidebar"
            />
          )}
        </View>

        <ScrollView style={styles.scrollArea} contentContainerStyle={styles.scrollContent} tabIndex={0}>
          <View style={[styles.sectionCards, { gap: isSidebarCollapsed ? 4 : 2 }]}>
            <WorkspaceCard
              {...homeWorkspace}
              isActive={homeWorkspace.id === activeWorkspaceId}
              onPress={() => handleWorkspacePress(homeWorkspace.id, onWorkspacePress)}
              isSidebarCollapsed={isSidebarCollapsed}
            />
          </View>
          {renderWorkspaceSection("GENERAL", generalWorkspaces)}
          {isSidebarCollapsed && !!courseWorkspaces.length && <View style={styles.collapsedCourseDivider} />}
          {renderWorkspaceSection("COURSE ASSISTANTS", courseWorkspaces, isLoadingCourseWorkspaces)}
        </ScrollView>

        <View style={styles.footer}>
          {todoWorkspaces.map((item) => (
            <WorkspaceCard
              key={item.id}
              {...item}
              isActive={item.id === activeWorkspaceId}
              onPress={() => handleWorkspacePress(item.id, onWorkspacePress)}
              isSidebarCollapsed={isSidebarCollapsed}
            />
          ))}
          {/* Commented out until backend is ready */}
          {/* {learningActivitiesWorkspaces.map((item) => (
            <WorkspaceCard
              key={item.id}
              {...item}
              isActive={item.id === activeWorkspaceId}
              onPress={() => handleWorkspacePress(item.id, onWorkspacePress)}
              isSidebarCollapsed={isSidebarCollapsed}
            />
          ))} */}
          {settingsWorkspaces.map((item) => (
            <WorkspaceCard
              key={item.id}
              {...item}
              isActive={item.id === activeWorkspaceId}
              onPress={() => handleWorkspacePress(item.id, onWorkspacePress)}
              isSidebarCollapsed={isSidebarCollapsed}
            />
          ))}
        </View>
        <View style={styles.footer}>
          <WorkspaceCard
            title="Back to courses"
            type="backToCourses"
            onPress={handleExternalPress}
            isSidebarCollapsed={isSidebarCollapsed}
          />
        </View>
      </View>
      {/* Backdrop */}
      {!isSidebarCollapsed && !isLarge && (
        <View style={styles.backdrop} onPointerDown={() => setIsSidebarCollapsed(true)} />
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  content: {
    flexDirection: "row",
    position: "relative",
    zIndex: 100,
  },
  container: {
    width: 272,
    borderRightWidth: 1,
    borderRightColor: colors.grey[200],
    justifyContent: "space-between",
    backgroundColor: colors.white,
  },
  containerSmall: {
    position: "absolute",
    top: 0,
    left: 0,
    bottom: 0,
    zIndex: 100,
    // @ts-expect-error - works for web
    height: "100vh",
  },
  backdrop: {
    position: "absolute",
    // @ts-expect-error - works for web
    width: "100vw",
    // @ts-expect-error - works for web
    height: "100vh",
    backgroundColor: "#00000080",
    zIndex: 99,
  },
  logoSection: {
    height: 57,
    paddingHorizontal: spacing.sm,
    borderBottomWidth: 1,
    borderBottomColor: colors.grey[200],
    backgroundColor: colors.white,
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  sidebarIcon: {
    borderRadius: "50%",
  },
  scrollArea: {
    flexGrow: 1,
  },
  scrollContent: {
    padding: spacing.xs,
  },
  sectionLabel: {
    fontSize: typographyScale.textXs.fontSize,
    fontWeight: "600",
    fontFamily: "Inter",
    letterSpacing: 0.8,
    textTransform: "uppercase",
    color: colors.grey[500],
    paddingHorizontal: 12,
    paddingVertical: spacing.xs,
  },
  sectionCards: {
    flexDirection: "column",
  },
  collapsedCourseDivider: {
    alignSelf: "center",
    width: 40,
    borderTopWidth: 1,
    borderTopColor: colors.grey[200],
    marginVertical: spacing.xs,
  },
  footer: {
    borderTopWidth: 1,
    borderTopColor: colors.grey[200],
    padding: spacing.xs,
  },
  skeletonCard: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingVertical: 6,
    paddingHorizontal: spacing.xs,
    borderRadius: radii.lg,
    backgroundColor: colors.white,
  },
  skeletonLeftSection: {
    flexDirection: "row",
    alignItems: "center",
    flex: 1,
    gap: spacing.xs,
  },
  skeletonText: {
    flex: 1,
  },
});

export default WorkspaceSidebar;
